

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';

export default function Billing() {
  const [clientData, setClientData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const data = localStorage.getItem('clientData');
    if (data) {
      setClientData(JSON.parse(data));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('clientData');
    navigate('/');
  };

  const invoices = [
    {
      id: '2024-001',
      date: '2024-01-18',
      dueDate: '2024-02-17',
      amount: 2500.00,
      status: 'pending',
      description: 'Backyard Renovation - Phase 2',
      project: 'Backyard Renovation'
    },
    {
      id: '2023-012',
      date: '2023-12-15',
      dueDate: '2024-01-14',
      amount: 3200.00,
      status: 'paid',
      description: 'Front Yard Landscaping - Final Payment',
      project: 'Front Yard Landscaping'
    },
    {
      id: '2023-011',
      date: '2023-11-20',
      dueDate: '2023-12-20',
      amount: 1800.00,
      status: 'paid',
      description: 'Front Yard Landscaping - Progress Payment',
      project: 'Front Yard Landscaping'
    },
    {
      id: '2023-010',
      date: '2023-11-01',
      dueDate: '2023-12-01',
      amount: 1500.00,
      status: 'paid',
      description: 'Front Yard Landscaping - Initial Payment',
      project: 'Front Yard Landscaping'
    }
  ];

  const paymentHistory = [
    {
      id: 1,
      date: '2024-01-10',
      amount: 3200.00,
      method: 'Credit Card',
      invoice: '2023-012',
      status: 'completed'
    },
    {
      id: 2,
      date: '2023-12-18',
      amount: 1800.00,
      method: 'Bank Transfer',
      invoice: '2023-011',
      status: 'completed'
    },
    {
      id: 3,
      date: '2023-11-28',
      amount: 1500.00,
      method: 'Credit Card',
      invoice: '2023-010',
      status: 'completed'
    }
  ];

  const handlePayInvoice = (invoiceId: string) => {
    // In a real app, this would integrate with Stripe or payment processor
    alert(`Payment processing for Invoice ${invoiceId} would be handled here. In a real application, this would connect to Stripe or another payment processor.`);
  };

  if (!clientData) {
    return <div>Loading...</div>;
  }

  const totalPending = invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + inv.amount, 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <div className="w-10 h-10 bg-red-700 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-hammer-line text-white"></i>
              </div>
              <h1 className="text-xl font-bold text-red-700" style={{ fontFamily: '"Pacifico", serif' }}>
                Chavez Castro
              </h1>
            </Link>
            
            <nav className="flex space-x-6">
              <Link to="/client/dashboard" className="text-gray-600 hover:text-red-700">
                Dashboard
              </Link>
              <Link to="/client/portfolio" className="text-gray-600 hover:text-red-700">
                My Portfolio
              </Link>
              <Link to="/client/billing" className="text-red-700 font-medium border-b-2 border-red-700">
                Billing
              </Link>
            </nav>

            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {clientData.name}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Payments</h1>
          <p className="text-gray-600">Manage your invoices and payment history</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-bill-line text-red-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Amount</p>
                <p className="text-2xl font-bold text-red-600">${totalPending.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">${totalPaid.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-list-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold text-blue-600">{invoices.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Invoices */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Invoices</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">Invoice #{invoice.id}</h3>
                        <p className="text-sm text-gray-600">{invoice.description}</p>
                        <p className="text-xs text-gray-500">{invoice.project}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">${invoice.amount.toLocaleString()}</p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                      <span>Date: {new Date(invoice.date).toLocaleDateString()}</span>
                      <span>Due: {new Date(invoice.dueDate).toLocaleDateString()}</span>
                    </div>
                    
                    {invoice.status === 'pending' && (
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          onClick={() => handlePayInvoice(invoice.id)}
                          className="flex-1"
                        >
                          <i className="ri-secure-payment-line mr-2"></i>
                          Pay Now
                        </Button>
                        <Button variant="outline" size="sm">
                          <i className="ri-download-line mr-2"></i>
                          Download
                        </Button>
                      </div>
                    )}
                    
                    {invoice.status === 'paid' && (
                      <Button variant="outline" size="sm" className="w-full">
                        <i className="ri-download-line mr-2"></i>
                        Download Receipt
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Payment History</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {paymentHistory.map((payment) => (
                  <div key={payment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">${payment.amount.toLocaleString()}</h3>
                        <p className="text-sm text-gray-600">Invoice #{payment.invoice}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Completed
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>{new Date(payment.date).toLocaleDateString()}</span>
                      <span className="flex items-center">
                        <i className="ri-bank-card-line mr-1"></i>
                        {payment.method}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <i className="ri-bank-card-line text-3xl text-blue-600 mb-2"></i>
              <h3 className="font-semibold text-gray-900">Credit Card</h3>
              <p className="text-sm text-gray-600">Visa, MasterCard, American Express</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <i className="ri-bank-line text-3xl text-green-600 mb-2"></i>
              <h3 className="font-semibold text-gray-900">Bank Transfer</h3>
              <p className="text-sm text-gray-600">Direct bank transfer (ACH)</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <i className="ri-money-dollar-circle-line text-3xl text-yellow-600 mb-2"></i>
              <h3 className="font-semibold text-gray-900">Check</h3>
              <p className="text-sm text-gray-600">Mail check to our office</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

